/**
 * The one staff sign-in form. Setup and badge printing used to carry a copy
 * each, which is why signing in twice to do two admin jobs was normal.
 */

import { useCallback, useId, useState, type FormEvent } from "react";
import { ApiError } from "../api/client";
import { useStaff } from "../context/StaffContext";

export default function StaffLoginForm() {
  const { signIn, expiredReason } = useStaff();
  const emailId = useId();
  const passwordId = useId();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      if (busy) return;
      setBusy(true);
      setError(null);
      try {
        await signIn(email.trim(), password);
        setPassword("");
      } catch (caught) {
        setError(loginErrorMessage(caught));
      } finally {
        setBusy(false);
      }
    },
    [busy, email, password, signIn],
  );

  return (
    <>
      {expiredReason && (
        <p className="admin-notice" role="alert">
          <span aria-hidden="true">⚠</span>
          {expiredReason}
        </p>
      )}

      <form className="admin-form" onSubmit={handleSubmit}>
        <div className="field">
          <label htmlFor={emailId}>Staff email</label>
          <input
            id={emailId}
            type="email"
            autoComplete="username"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
        </div>
        <div className="field">
          <label htmlFor={passwordId}>Password</label>
          <input
            id={passwordId}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
        </div>

        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}

        <div className="admin-form-actions">
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </div>
      </form>
    </>
  );
}

function loginErrorMessage(caught: unknown): string {
  if (caught instanceof ApiError) {
    if (caught.isNetwork) return "Could not reach the server. Check the network.";
    if (caught.httpStatus === 400 || caught.httpStatus === 401) {
      return "Login failed — check your email and password.";
    }
    return caught.message;
  }
  return "Something went wrong. Try again.";
}
